import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconComponent, IconType } from '../icons/IconComponent';
import dayjs from 'shared/src/tools/Dayjs';
import { getMailReportForDate, MailReport, MailServiceReport, MailType } from 'shared';
import { useUser } from '../UserContext';
import { Popup, Table, TableRow } from '@sam/components';
import { TableHeader } from '@sam/components/src/Table/Table.types';

export const MailReportWidget: React.FC = () => {
  const { axios } = useUser();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [serviceReport, setServiceReport] = useState<MailServiceReport>();
  const { t } = useTranslation();
  const [popupType, setPopupType] = useState<MailType>();

  /**
   * Hook to load the report for the selected day
   */
  useEffect(() => {
    getMailReportForDate(axios, selectedDate).then((loadedReport) =>
      setServiceReport(loadedReport),
    );
  }, [selectedDate]);

  /**
   * Holds the entries for a specific mailType if selected
   */
  const popupRows: TableRow[] = useMemo((): TableRow[] => {
    if (!popupType) return [];
    const report: MailReport | undefined = serviceReport?.reports.find(
      (report) => report.type === popupType,
    );
    if (!report) return [];
    const rows: TableRow[] = report.failedInfos.map((entry) => ({
      id: entry.id,
      content: [entry.subject, entry.receiver],
      background: '#bc2e4690',
    }));
    report.successInfos.forEach((entry, index) =>
      rows.push({
        id: entry.receiver + entry.receiver + index,
        content: [entry.subject, entry.receiver],
      }),
    );
    return rows;
  }, [popupType, serviceReport]);

  return (
    <div className="widget-component__wrapper">
      <Popup
        children={
          <Table
            rows={popupRows}
            header={
              t('pages.dashboard.widgets.reportPopupHeader', {
                returnObjects: true,
              }) as TableHeader[]
            }
          />
        }
        isOpen={!!popupType}
        onClose={() => setPopupType(undefined)}
        title={t(`general.mailTypes.${popupType}`)}
      />
      <div className="widget-component__headline-wrapper">
        <IconComponent
          type={IconType.ARROW_LEFT}
          width={10}
          onClick={() => setSelectedDate(dayjs(selectedDate).subtract(1, 'day').toDate())}
          className="clickable"
        />
        <p className="widget-component__headline">
          {t('pages.dashboard.widgets.headlineMailReport', {
            replace: { DATE: selectedDate?.toLocaleDateString('DE-de') },
          })}
        </p>
        <IconComponent
          type={IconType.ARROW_RIGHT}
          width={10}
          onClick={() => setSelectedDate(dayjs(selectedDate).add(1, 'day').toDate())}
          className="clickable"
        />
      </div>
      <ul>
        {serviceReport?.reports?.map((report) => (
          <li
            className="widget-component__mail-entry clickable"
            onClick={() => setPopupType(report.type)}
            key={report.id}
          >
            <p
              className={`widget-component__mail-entry__title ${report.failedInfos.length === 0 ? 'success' : 'failed'}`}
            >
              {t(`general.mailTypes.${report.type}`)}
            </p>
            <p
              className={`widget-component__mail-entry__result ${report.failedInfos.length === 0 ? 'success' : 'failed'}`}
            >{`${report.successInfos.length} / ${report.failedInfos.length + report.successInfos.length}`}</p>
          </li>
        ))}
      </ul>
    </div>
  );
};
